/**
 * API Client for making requests to the GDB backend.
 *
 * Cloud Edition: same-origin by default. In dev, Vite proxies /api/* to the
 * backend. In production, Axum serves both the SPA and the API.
 *
 * Phase 17.5 (cloud-SPA dev mode): when the C++ shell loads the SPA from
 * https://app.gamedevbuddy.com with `?apiBase=http%3A%2F%2Flocalhost%3A<port>`,
 * apiUrl() redirects every fetch at the LOCAL sidecar. URL composition lives
 * in `lib/api/apiUrl.ts` — this client just threads it through.
 */

import { apiUrl, authUrl } from './api/apiUrl'

const TOKEN_KEY = 'gdb_token'

/**
 * Shared fetch core: attach the JSON content-type + Bearer token, then fetch
 * the already-composed absolute (or same-origin) URL. Both apiRequest (sidecar
 * base) and authRequest (cloud base) thread their URL through here so the
 * header/token behaviour can never drift between the two.
 */
async function requestWithToken(
  url: string,
  options?: RequestInit
): Promise<Response> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  // Auto-attach Bearer token if present
  const token = localStorage.getItem(TOKEN_KEY)
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  return fetch(url, {
    ...options,
    headers: {
      ...headers,
      ...(options?.headers as Record<string, string> | undefined),
    },
  })
}

/**
 * Get the base URL prefix for all API requests. Backward-compat shim — most
 * callers should use `apiUrl(path)` directly instead. Returns '' in bundled
 * / dev-server mode and the sidecar base URL in cloud-SPA mode.
 */
export function getApiBaseUrl(): string {
  return apiUrl('')
}

/**
 * Make an API request with the correct base URL, JSON content type,
 * and automatic Bearer token attachment from localStorage.
 */
export async function apiRequest(
  path: string,
  options?: RequestInit
): Promise<Response> {
  return requestWithToken(apiUrl(path), options)
}

/**
 * Make a CLOUD request — targets the cloud (authUrl), not the sidecar.
 *
 * The cloud owns auth (login/register/refresh/status/forgot-password/oauth)
 * AND every other cloud-resident concern: account/profile, the /console admin
 * surface, purchases/products, Discord, and the marketing + console CMS content
 * (contact, suggestions, news, stumpy). All of that goes DIRECT to the cloud
 * now that the sidecar auth proxy is gone. Only LOCAL/on-disk data — the bridge,
 * workspace, the Oracle vault, and local module install — stays on apiRequest →
 * the sidecar. Same JSON content-type + Bearer attachment as apiRequest, just a
 * different base URL.
 *
 * Deliberate `apiRequest` (sidecar) exceptions, by design:
 *  - logout — must clear the SIDECAR's held token; the cloud has no logout route.
 *  - module download / local-module install — the sidecar mediates putting the
 *    plugin on local disk, so those are genuinely sidecar operations.
 */
export async function authRequest(
  path: string,
  options?: RequestInit
): Promise<Response> {
  return requestWithToken(authUrl(path), options)
}

/** Convenience methods for common HTTP verbs. */
export const api = {
  get: (path: string, headers?: Record<string, string>) =>
    apiRequest(path, headers ? { headers } : undefined),

  post: (path: string, body?: unknown, headers?: Record<string, string>) =>
    apiRequest(path, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...(headers ? { headers } : {}),
    }),

  put: (path: string, body?: unknown) =>
    apiRequest(path, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  delete: (path: string) =>
    apiRequest(path, {
      method: 'DELETE',
    }),

  patch: (path: string, body?: unknown) =>
    apiRequest(path, {
      method: 'PATCH',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),
}

/**
 * Convenience methods for CLOUD verbs — same shape as `api`, but every call
 * targets the CLOUD via authRequest (NOT the sidecar). Use for ALL cloud-owned
 * data: auth, account, the /console admin surface, purchases/products, Discord,
 * and the marketing/console CMS content. The Bearer token is still attached.
 * (Local/on-disk data uses `api` above; see authRequest for the deliberate
 * sidecar exceptions — logout and module install.)
 */
export const authClient = {
  get: (path: string, headers?: Record<string, string>) =>
    authRequest(path, headers ? { headers } : undefined),

  post: (path: string, body?: unknown, headers?: Record<string, string>) =>
    authRequest(path, {
      method: 'POST',
      body: body !== undefined ? JSON.stringify(body) : undefined,
      ...(headers ? { headers } : {}),
    }),

  put: (path: string, body?: unknown) =>
    authRequest(path, {
      method: 'PUT',
      body: body !== undefined ? JSON.stringify(body) : undefined,
    }),

  delete: (path: string) =>
    authRequest(path, {
      method: 'DELETE',
    }),
}
